import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getLicenseDb, redirectToLicenseEndpoint, getCustomerId, useLicenseStatus, upgradeToBundle } from "./license";

export default function EstimatorGate({ S, C, onBack }) {
  const [status, setStatus] = useState("loading"); // loading | active | inactive
  const [busy, setBusy] = useState(false);
  const suppliesStatus = useLicenseStatus("supplies"); // loading | active | inactive
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleErr,  setBundleErr]  = useState("");

  useEffect(() => {
    // A misconfigured/unreachable license service (e.g. LICENSE_FIREBASE_CONFIG
    // still has placeholder values) can leave the listener pending without ever
    // calling its error callback — fail safe to "inactive" rather than hang on
    // "Loading…" forever.
    const timeout = setTimeout(() => setStatus((s) => (s === "loading" ? "inactive" : s)), 6000);
    const licenseDb = getLicenseDb();
    const unsub = onSnapshot(
      doc(licenseDb, "licenses", getCustomerId()),
      (snap) => {
        clearTimeout(timeout);
        const data = snap.exists() ? snap.data() : null;
        setStatus(data?.estimator?.status === "active" ? "active" : "inactive");
      },
      () => {
        clearTimeout(timeout);
        setStatus("inactive");
      }
    );
    return () => { clearTimeout(timeout); unsub(); };
  }, []);

  const redirectTo = async (endpoint, extraBody) => {
    setBusy(true);
    const redirected = await redirectToLicenseEndpoint(endpoint, extraBody);
    if (!redirected) setBusy(false);
  };

  const subscribe     = () => redirectTo("createCheckoutSession", { product: "estimator", successUrl: window.location.href, cancelUrl: window.location.href });
  const subscribeBundle = () => redirectTo("createCheckoutSession", { product: "bundle", successUrl: window.location.href, cancelUrl: window.location.href });
  const manageBilling = () => redirectTo("createPortalSession",  { returnUrl: window.location.href });
  // Adds Job Estimator to an already-active Supply Tracker subscription —
  // swaps that same subscription onto the $25/mo bundle price instead of
  // starting a second one (see upgradeToBundle). $5/mo cheaper than paying
  // for both separately ($25 + $5 = $30).
  const addEstimatorViaBundle = async () => {
    setBundleBusy(true); setBundleErr("");
    try { await upgradeToBundle(); }
    catch (e) { setBundleErr(e.message || "Failed to upgrade — try again."); }
    setBundleBusy(false);
  };

  if (status === "loading") {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, color:C.textMid, fontSize:"14px" }}>
        Loading…
      </div>
    );
  }

  if (status === "active") {
    return (
      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0 }}>
        <div style={{ display:"flex", justifyContent:"flex-end", padding:"8px 12px", borderBottom:`1px solid ${C.border}`, background:C.white }}>
          <button onClick={manageBilling} disabled={busy} style={S.btnSm(C.offWhite, C.textMid)}>
            {busy ? "Loading…" : "Manage billing"}
          </button>
        </div>
        <iframe
          src="/admin-estimator.html"
          style={{ width:"100%", flex:1, border:"none", display:"block" }}
          title="Job Estimator"
        />
      </div>
    );
  }

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0 }}>
      {onBack && (
        <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}`, background:C.white }}>
          <button onClick={onBack} style={S.btnSm(C.offWhite, C.textMid)}>← Back</button>
        </div>
      )}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", flex:1, padding:"24px" }}>
        <div style={{ ...S.card, textAlign:"center", maxWidth:"380px" }}>
          <div style={{ fontSize:"32px", marginBottom:"10px" }}>🧮</div>
          <div style={{ ...S.title, textAlign:"center" }}>Unlock the Job Estimator</div>
          <div style={{ ...S.sub, textAlign:"center" }}>
            Build accurate quotes and generate client-ready proposals, tuned to your own pricing.
          </div>
          {suppliesStatus === "active" ? (<>
            <div style={{ ...S.sub, textAlign:"center", marginTop:"4px" }}>
              You already pay $5/mo for Supply Tracker — upgrade to the $25/mo bundle to add this too, $5/mo cheaper than subscribing separately.
            </div>
            {bundleErr && <div style={S.err}>{bundleErr}</div>}
            <button style={S.btn} onClick={addEstimatorViaBundle} disabled={bundleBusy}>
              {bundleBusy ? "Upgrading…" : "Upgrade to bundle — $25/mo total"}
            </button>
            <button style={{ ...S.btnSm(C.offWhite, C.textMid), marginTop:"8px" }} onClick={subscribe} disabled={busy}>
              {busy ? "Redirecting…" : "Or subscribe separately — $25/mo"}
            </button>
          </>) : (<>
            <button style={S.btn} onClick={subscribe} disabled={busy}>
              {busy ? "Redirecting…" : "Subscribe to unlock"}
            </button>
            {suppliesStatus === "inactive" && (
              <div style={{ fontSize:"12px", color:C.textMid, marginTop:"10px" }}>
                Also want Supply Tracker? <button style={{ ...S.btnSm("#EEF3FB",C.accent), marginLeft:"4px" }} onClick={subscribeBundle} disabled={busy}>Get both for $25/mo</button>
              </div>
            )}
          </>)}
          {onBack && (
            <button style={{ ...S.ghostBtn, marginTop:"10px" }} onClick={onBack}>Maybe later</button>
          )}
        </div>
      </div>
    </div>
  );
}
